import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Errore imprevisto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputClass =
  "w-full bg-[#0A0E14] border border-white/15 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C8D7] transition-colors";

export default function ChangePasswordModal({ open, onClose, token }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    if (next !== confirm) {
      toast.error("Le due nuove password non coincidono.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password aggiornata con successo.");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
          onClick={onClose}
          data-testid="change-password-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#111827] border border-white/10 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center bg-[#00C8D7]/10 text-[#00C8D7] border border-[#00C8D7]/30">
                  <KeyRound size={18} />
                </span>
                <h2 className="font-display font-bold text-xl tracking-tight">Cambia password</h2>
              </div>
              <button data-testid="change-password-close" onClick={onClose} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submit} data-testid="change-password-form" className="space-y-4">
              <input
                data-testid="cp-current"
                type="password"
                className={inputClass}
                placeholder="Password attuale"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
              <input
                data-testid="cp-new"
                type="password"
                className={inputClass}
                placeholder="Nuova password (min. 8 caratteri)"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
              <input
                data-testid="cp-confirm"
                type="password"
                className={inputClass}
                placeholder="Conferma nuova password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <button
                data-testid="cp-submit"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-8 py-3.5 hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                Aggiorna password
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
